import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider,
    Button,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import { AgentConfig, Feature } from "./agentConfig";

interface AgentChatProps {
    isOpen: boolean;
    onClose: () => void;
    config: AgentConfig;
    onStartTour: (feature?: Feature) => void;
}

const AgentChat: React.FC<AgentChatProps> = ({
    isOpen,
    onClose,
    config,
    onStartTour,
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <Paper
                    component={motion.div}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.9 }}
                    elevation={6}
                    sx={{
                        position: "fixed",
                        bottom: 90,
                        right: 24,
                        width: 320,
                        maxHeight: 500,
                        zIndex: 9998,
                        overflow: "hidden",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "primary.main",
                            color: "white",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                            {config.projectName} Assistant
                        </Typography>
                        <Button
                            size="small"
                            onClick={onClose}
                            sx={{ minWidth: "auto", color: "white" }}
                        >
                            <CloseIcon fontSize="small" />
                        </Button>
                    </Box>

                    <Box sx={{ p: 2, flexGrow: 1, overflowY: "auto" }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {config.welcomeMessage}
                        </Typography>

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => onStartTour()}
                            sx={{ mb: 2 }}
                        >
                            Start Full Presentation
                        </Button>

                        <Divider sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                OR CHOOSE A TOPIC
                            </Typography>
                        </Divider>

                        <List dense>
                            {config.features.map((feature) => (
                                <ListItem key={feature.id} disablePadding>
                                    <ListItemButton onClick={() => onStartTour(feature)}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <PlayArrowIcon fontSize="small" color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature.title}
                                            secondary={feature.description}
                                            primaryTypographyProps={{ variant: "subtitle2" }}
                                            secondaryTypographyProps={{
                                                variant: "caption",
                                                noWrap: true,
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Paper>
            )}
        </AnimatePresence>
    );
};

export default AgentChat;
